import { useCallback, useMemo } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { useEditorContext } from '@editor/contexts/Editor'
import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { zeroPad } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems } from '@editor/store/selectors'
import { removeSuccessTool } from '@editor/store/tool'

import { usePolylineSelector } from '../../store/selector'
import { CRACK_DETECTION_PCD_ID, CRACK_DETECTION_POLYGON_ID } from '../constants'
import { CrackDetectionStage, DetectionKind } from '../interfaces'
import { getDetectionKind, hasDetectionResult, resetCurrent } from '../store/crackDetection'
import {
  clearEditedPolylines,
  deleteCompletedPolylines,
  deleteCompletedPolylinesByJobId,
  removeWorkingPolyline,
  upsertEditedPolyline,
} from '../store/polyline'

const useElementsPanel = (): ElementsPanelConfig => {
  // Context
  const { selectedTool } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const permissionSet = useAppSelector((state) => state.editor.permissionSet)
  const inspectionItems = useAppSelector(selectInspectionItems)

  const isSaving = usePolylineSelector((state) => state.core.isSaving)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)
  const savingPolylines = usePolylineSelector((state) => state.polyline.savingPolylines)
  const updatingPolylines = usePolylineSelector((state) => state.polyline.updatingPolylines)
  const workingPoints = usePolylineSelector((state) => state.polyline.workingPoints)
  const workingPointCrackDetectionId = usePolylineSelector((state) => state.polyline.workingPointCrackDetectionId)

  const crackDetection = usePolylineSelector((state) => state.crackDetection.executions[state.crackDetection.areaId])

  // Permission set
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)
  const isToolSelected = selectedTool === EDITOR_TOOLS.POLYLINE

  const polylines = useMemo(() => inspectionItems.filterByType(InspectionItemType.PolylineLength), [inspectionItems])

  /**
   * Handle deletion of a saved polyline while tool is selected.
   * This will only mark the polyline for deletion and not directly delete it
   * as when user is not in Polyline tool.
   */
  const handleDeleteSavedPolyline = useCallback(
    (inspectionItem: InspectionItem) => {
      if (!isAllowedToModify || !isToolSelected) return

      dispatch(
        upsertEditedPolyline({
          id: inspectionItem.inspection_item_id!,
          points: [],
          isDeleted: true,
        }),
      )
    },
    [isAllowedToModify, isToolSelected, dispatch],
  )

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']): EditorPanelItem[] => {
        // Polyline is parent-level, ignore if parent is provided
        if (parent) return []

        const panelItems: EditorPanelItem[] = []

        const workingPanelItem = {
          key: `panel-polyline-working`,
          name: `延長${zeroPad(polylines.length + completedPolylines.length + 1, 3)}`,
          isWorking: true,
          toggleVisibility: null,
          onDelete: () => dispatch(removeWorkingPolyline()),
        }

        // -- Add Polylines items
        if (polylines.length || savingPolylines.length || completedPolylines.length || workingPoints.length) {
          panelItems.push({
            key: 'panel-polyline',
            name: '延長',
            isVirtualContainer: true,
            isCountable: true,
            children: [
              ...polylines.map<EditorPanelItem>((item) => {
                const edited = editedPolylines.find((polyline) => polyline.id === item.inspection_item_id)
                const isWorking = edited !== undefined
                const isSavingPolyline = updatingPolylines.includes(item.inspection_item_id!)
                const onDelete =
                  isToolSelected && !edited?.isDeleted
                    ? handleDeleteSavedPolyline.bind(null, item)
                    : !updatingPolylines.includes(item.inspection_item_id!) && !isWorking && isAllowedToModify

                return {
                  key: `panel-polyline-${item.inspection_item_id}`,
                  name: item.part_name,
                  item,
                  inspectionItem: item,
                  order: item.order_in_inspection_group,
                  isGrouped: !!item.inspection_group_id,
                  isOrderable: !isWorking,
                  isWorking,
                  isSaving: updatingPolylines.includes(item.inspection_item_id!),
                  onDelete,
                  onReset:
                    isWorking && !isSavingPolyline
                      ? () => dispatch(clearEditedPolylines([item.inspection_item_id!]))
                      : undefined,
                  isMarkedForDeletion: edited?.isDeleted ?? false,
                }
              }),
              ...savingPolylines.map<EditorPanelItem>((item, index) => ({
                id: item.id,
                key: `panel-polyline-saving-${item.id}`,
                name: `延長${zeroPad(index + polylines.length + 1, 3)}`,
                isWorking: true,
                isSaving: true,
                onDelete: false, // Cannot delete while saving
                toggleVisibility: true,
              })),
              ...completedPolylines
                .map<EditorPanelItem | null>((item, index) =>
                  !item.jobId
                    ? {
                        id: item.id,
                        itemType: InspectionItemType.PolylineLength,
                        groupId: item.groupId,
                        key: `panel-polyline-working-${item.id}`,
                        name: `延長${zeroPad(index + savingPolylines.length + polylines.length + 1, 3)}`,
                        isGrouped: !!item.groupId,
                        isWorking: true,
                        toggleVisibility: true,
                        onDelete: () => dispatch(deleteCompletedPolylines([item.id])),
                      }
                    : null,
                )
                .filter((item): item is EditorPanelItem => item !== null),
              ...(workingPoints.length && !workingPointCrackDetectionId ? [workingPanelItem] : []),
            ],
          })
        }

        // -- Add Crack Detection items
        if (
          crackDetection &&
          ![CrackDetectionStage.None, CrackDetectionStage.SavingPolylines].includes(crackDetection.status) &&
          !isSaving
        ) {
          const isRunning = [CrackDetectionStage.Requesting, CrackDetectionStage.Polling].includes(
            crackDetection.status,
          )
          // A chalk run has no point cloud, so "produced a result" cannot be read off `pcdURL`.
          const hasResult = hasDetectionResult(crackDetection)
          const isSuccess = hasResult && !isToolSelected

          const crackPanelItem: EditorPanelItem = {
            key: 'panel-polyline-crack-detection',
            name: getDetectionKind(crackDetection) === DetectionKind.Chalk ? 'チョーキング検出' : 'クラック検出',
            isVirtualContainer: isToolSelected,
            isWorking: !isSuccess,
            isSuccess,
            isLoading: isRunning,
            toggleVisibility: true,
            children: [],
            onDelete: hasResult
              ? () => {
                  dispatch(deleteCompletedPolylinesByJobId(crackDetection.id))
                  dispatch(resetCurrent())
                  dispatch(removeSuccessTool(EDITOR_TOOLS.POLYLINE))
                }
              : undefined,
          }

          if (crackDetection.points.length) {
            crackPanelItem.children!.push({
              id: CRACK_DETECTION_POLYGON_ID,
              key: `panel-${CRACK_DETECTION_POLYGON_ID}`,
              name: `検出エリア`,
              toggleVisibility: true,
            })
          }

          if (isToolSelected) {
            if (crackDetection.pcdURL) {
              crackPanelItem.children!.push({
                id: CRACK_DETECTION_PCD_ID,
                key: `panel-${CRACK_DETECTION_PCD_ID}`,
                name: `点群`,
                toggleVisibility: true,
              })
            }

            const polylineContainerItem: EditorPanelItem = {
              key: `panel-polyline-crack-detection-polyline`,
              name: `延長`,
              isVirtualContainer: true,
              children: [],
            }

            if (completedPolylines.filter(({ jobId }) => jobId).length) {
              polylineContainerItem.children!.push(
                ...completedPolylines
                  .map<EditorPanelItem | null>((item, index) =>
                    item.jobId
                      ? {
                          id: item.id,
                          itemType: InspectionItemType.PolylineLength,
                          key: `panel-polyline-crack-detection-${item.id}`,
                          name: `延長${zeroPad(index + savingPolylines.length + polylines.length + 1, 3)}`,
                          isGrouped: !!item.groupId,
                          isWorking: true,
                          toggleVisibility: true,
                          onDelete: () => dispatch(deleteCompletedPolylines([item.id])),
                        }
                      : null,
                  )
                  .filter((item): item is EditorPanelItem => item !== null),
              )
            }

            if (workingPoints.length && workingPointCrackDetectionId) {
              polylineContainerItem.children!.push(workingPanelItem)
            }

            crackPanelItem.children!.push(polylineContainerItem)
          }

          panelItems.push(crackPanelItem)
        }

        return panelItems
      },
      [
        isSaving,
        polylines,
        savingPolylines,
        editedPolylines,
        completedPolylines,
        workingPoints,
        isToolSelected,
        isAllowedToModify,
        updatingPolylines,
        crackDetection,
        workingPointCrackDetectionId,
        handleDeleteSavedPolyline,
        dispatch,
      ],
    ),
  }
}

export default useElementsPanel
